import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { IconCircleCheck } from '@edulastic/icons'
import { chalkboard600, white } from '@edulastic/colors'
import {
  getFeedbackCharacterCount,
  MAX_FEEDBACK_CHARACTERS,
  trimToCharacterLimit,
} from '../ActionHub/actionHubHelpers'
import { FEEDBACK_TYPES } from './constants'
import {
  FEEDBACK_MODAL_WRAP_CLASS,
  DiscardButton,
  DiscardConfirmFooter,
  DiscardConfirmMessage,
  DiscardConfirmModal,
  FeedbackCustomModal,
  FeedbackModalGlobalStyle,
  FeedbackTextArea,
  FeedbackFooterContent,
  FormContainer,
  KeepEditingButton,
  SubmitAnotherButton,
  SubmitButton,
  SuccessContainer,
  SuccessIconWrapper,
  SuccessMessage,
  WordCountText,
  FailureContainer,
  FailureIcon,
  FailureIconWrapper,
  FailureMessage,
} from './styled'

const MODAL_STATES = {
  FORM: 'form',
  SUCCESS: 'success',
  FAILURE: 'failure',
}

const FeedbackModal = ({
  visible,
  onClose,
  onSubmit,
  onOpen,
  onSubmitSuccess,
  onSubmitFailure,
}) => {
  const [feedbackText, setFeedbackText] = useState('')
  const [modalState, setModalState] = useState(MODAL_STATES.FORM)
  const [submitting, setSubmitting] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [hasSubmittedInCurrentFlow, setHasSubmittedInCurrentFlow] = useState(
    false
  )

  const characterCount = getFeedbackCharacterCount(feedbackText)
  const isOverLimit = characterCount > MAX_FEEDBACK_CHARACTERS
  const canSubmit = characterCount > 0 && !isOverLimit && !submitting
  const hasUnsavedChanges =
    visible &&
    modalState === MODAL_STATES.FORM &&
    feedbackText.trim().length > 0

  const handleFeedbackChange = (event) => {
    const newValue = event.target.value
    if (getFeedbackCharacterCount(newValue) <= MAX_FEEDBACK_CHARACTERS) {
      setFeedbackText(newValue)
      return
    }

    const isPaste = newValue.length - feedbackText.length > 1
    const isDelete = newValue.length < feedbackText.length
    if (isPaste || isDelete) {
      setFeedbackText(newValue)
    }
  }

  useEffect(() => {
    if (visible) {
      onOpen?.()
    }
  }, [visible, onOpen])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const resetForm = () => {
    setFeedbackText('')
    setModalState(MODAL_STATES.FORM)
    setSubmitting(false)
    setShowDiscardConfirm(false)
    setHasSubmittedInCurrentFlow(false)
  }

  const handleClose = () => {
    const shouldTrackClose = !hasSubmittedInCurrentFlow
    resetForm()
    onClose?.({ shouldTrackClose })
  }

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true)
      return
    }

    handleClose()
  }

  const handleDiscard = () => {
    handleClose()
  }

  const handleKeepEditing = () => {
    setShowDiscardConfirm(false)
  }

  const handleSubmitAnother = () => {
    setFeedbackText('')
    setModalState(MODAL_STATES.FORM)
    setHasSubmittedInCurrentFlow(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    setSubmitting(true)
    try {
      const feedback = trimToCharacterLimit(feedbackText)
      const search = window.location.search || ''
      await onSubmit?.({
        feedbackType: FEEDBACK_TYPES.GENERAL,
        feedback,
        pagePath: window.location.pathname,
        urlParam: search.startsWith('?')
          ? search.slice(1)
          : search || undefined,
      })
      setHasSubmittedInCurrentFlow(true)
      setModalState(MODAL_STATES.SUCCESS)
      onSubmitSuccess?.({ feedbackDetails: feedback })
    } catch (e) {
      setModalState(MODAL_STATES.FAILURE)
      onSubmitFailure?.()
    } finally {
      setSubmitting(false)
    }
  }

  const renderContent = () => {
    if (modalState === MODAL_STATES.SUCCESS) {
      return (
        <SuccessContainer data-cy="feedback-success-state">
          <SuccessIconWrapper>
            <IconCircleCheck
              width={48}
              height={48}
              color={chalkboard600}
              checkColor={white}
              aria-hidden
            />
          </SuccessIconWrapper>
          <SuccessMessage>
            Feedback submitted
            <br />
            successfully
          </SuccessMessage>
        </SuccessContainer>
      )
    }

    if (modalState === MODAL_STATES.FAILURE) {
      return (
        <FailureContainer data-cy="feedback-failure-state">
          <FailureIconWrapper>
            <FailureIcon aria-hidden />
          </FailureIconWrapper>
          <FailureMessage>Submission failed</FailureMessage>
          <FailureMessage>
            We were unable to submit your feedback. Please try again later.
          </FailureMessage>
        </FailureContainer>
      )
    }

    return (
      <FormContainer>
        <div>
          <FeedbackTextArea
            value={feedbackText}
            onChange={handleFeedbackChange}
            placeholder="Enter feedback here"
            data-cy="action-hub-feedback-input"
          />
          <WordCountText
            $exceeded={isOverLimit}
            data-cy="feedback-character-count"
          >
            {characterCount}/{MAX_FEEDBACK_CHARACTERS} characters
          </WordCountText>
        </div>

        <SubmitButton
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-cy="feedback-submit-btn"
        >
          {submitting ? 'SUBMITTING...' : 'SUBMIT'}
        </SubmitButton>
      </FormContainer>
    )
  }

  const renderFooter = () => {
    if (modalState === MODAL_STATES.SUCCESS) {
      return (
        <FeedbackFooterContent>
          <SubmitAnotherButton
            type="button"
            onClick={handleSubmitAnother}
            data-cy="feedback-submit-another-btn"
          >
            Submit another Feedback
          </SubmitAnotherButton>
        </FeedbackFooterContent>
      )
    }

    if (modalState === MODAL_STATES.FAILURE) {
      return (
        <FeedbackFooterContent>
          <SubmitAnotherButton
            type="button"
            onClick={handleClose}
            data-cy="feedback-close-btn"
          >
            CLOSE
          </SubmitAnotherButton>
        </FeedbackFooterContent>
      )
    }

    return null
  }

  const isResultView =
    modalState === MODAL_STATES.SUCCESS || modalState === MODAL_STATES.FAILURE

  return (
    <>
      <FeedbackModalGlobalStyle />
      <FeedbackCustomModal
        visible={visible}
        title="Give Feedback"
        onCancel={handleCloseAttempt}
        mask={false}
        maskClosable={false}
        getContainer={() => document.body}
        wrapClassName={FEEDBACK_MODAL_WRAP_CLASS}
        zIndex={1100}
        width={348}
        modalWidth="348px"
        titleFontSize="20px"
        titleFontWeight={600}
        headerPadding="24px"
        footer={renderFooter()}
        destroyOnClose
        $bodyPadding={isResultView ? '0' : '24px'}
        data-cy="action-hub-feedback-modal"
      >
        {renderContent()}
      </FeedbackCustomModal>
      <DiscardConfirmModal
        visible={showDiscardConfirm}
        title="Leave without submitting?"
        onCancel={handleKeepEditing}
        centered
        maskClosable={false}
        getContainer={() => document.body}
        zIndex={1200}
        width={400}
        modalWidth="400px"
        footer={
          <DiscardConfirmFooter>
            <KeepEditingButton
              type="button"
              onClick={handleKeepEditing}
              data-cy="feedback-keep-editing-btn"
            >
              Keep Editing
            </KeepEditingButton>
            <DiscardButton
              type="button"
              onClick={handleDiscard}
              data-cy="feedback-discard-btn"
            >
              Discard
            </DiscardButton>
          </DiscardConfirmFooter>
        }
        data-cy="feedback-discard-confirm-modal"
      >
        <DiscardConfirmMessage>
          Your feedback won&apos;t be saved unless you submit it.
        </DiscardConfirmMessage>
      </DiscardConfirmModal>
    </>
  )
}

FeedbackModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onOpen: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  onSubmitFailure: PropTypes.func,
}

FeedbackModal.defaultProps = {
  visible: false,
  onClose: () => {},
  onSubmit: () => {},
  onOpen: () => {},
  onSubmitSuccess: () => {},
  onSubmitFailure: () => {},
}

export default FeedbackModal
